/**
 * Task 3.7 — "Apply same time to all" banner.
 *
 * Visible only when the parent drawer's trigger condition is true (computed
 * in `AssignTeammatesDrawer.tsx`). The banner copy is
 * `"Apply this schedule to all selected members?"` (DD-5434).
 *
 * Buttons are icon-only ✔ / ✖ (locked). Background uses
 * `theme.palette["warning-minor"].tint` (`#FFF4CB`) — the closest existing
 * cream/yellow tint in the theme, matches image 6.
 *
 * Pure presentational — all state (visibility, dismiss flag, apply mutation)
 * lives in the drawer's local state.
 */

import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { CheckIcon, CloseIcon, InfoFilledIcon } from "components/icons";

interface ApplySameTimeBannerProps {
  visible: boolean;
  onApply: () => void;
  onCancel: () => void;
}

export const ApplySameTimeBanner = ({
  visible,
  onApply,
  onCancel,
}: ApplySameTimeBannerProps) => {
  const theme = useTheme();

  if (!visible) return null;

  // `warning-minor.tint` (#FFF4CB) is the cream/yellow token (declared in
  // src/theme.d.ts and src/utilz/theme/theme.ts) — closest match to image 6.
  const bannerBg = theme.palette["warning-minor"].tint;
  const accent = theme.palette["warning-minor"].main;

  return (
    <Stack
      direction="row"
      alignItems="center"
      role="status"
      sx={{
        m: "8px 16px",
        p: "8px 12px",
        borderRadius: "8px",
        backgroundColor: bannerBg,
        gap: "8px",
      }}
    >
      <InfoFilledIcon sx={{ color: accent, fontSize: "20px" }} />
      <Typography
        variant="body-medium"
        sx={{ flex: 1, color: theme.palette.lightBg.high }}
      >
        Apply this schedule to all selected members?
      </Typography>
      <Box sx={{ display: "flex", gap: "4px" }}>
        <IconButton
          size="small"
          onClick={onApply}
          aria-label="Apply same time to all selected members"
          sx={{
            color: theme.palette.lightBg.medium,
            "&:hover": {
              color: theme.palette.success.main,
              backgroundColor: theme.palette.success.tint,
            },
          }}
        >
          <CheckIcon sx={{ fontSize: "18px" }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={onCancel}
          aria-label="Dismiss"
          sx={{
            color: theme.palette.lightBg.medium,
            "&:hover": {
              color: theme.palette.error.main,
              backgroundColor: theme.palette.error.tint,
            },
          }}
        >
          <CloseIcon sx={{ fontSize: "18px" }} />
        </IconButton>
      </Box>
    </Stack>
  );
};
